import {Async, Empty, isLoaded, Loaded} from 'Core/models/Async';
import {
  GpluAgeBandsAndSmokingFlags,
  RetrievePlanConfigurationRequestPayload,
  RetrievePlansRequestData,
} from 'Quoting/api/types';
import {addNewPlanToQuote as addNewPlanToQuoteACC} from 'Quoting/components/QuoteForm/Plan/PlanTemplates/ACC/GAI7';
import {MISSING_PLAN_ERROR_KEY} from 'Quoting/components/QuoteForm/Plan/PlanTemplates/GWL/gpluConstants';
import {addNewPlanToQuote} from 'Quoting/components/QuoteForm/Plan/PlanTemplates/addPlanUtility';
import {CensusClassDTO} from 'Quoting/models/DTOs/Opportunity';
import {
  CreateQuoteRequestPayload,
  EntityMinMaxConfig,
  QuoteFormDropdownOption,
  RetreivedPlan,
} from 'Quoting/models/DTOs/QuoteForm';
import {RatePremiumDetails} from 'Quoting/models/DTOs/RatePremium';
import {MasterPolicyQuote} from 'Quoting/models/DTOs/quote';
import {A360ProductCd, A360ProductModel, Product} from 'Quoting/models/Quoting';
import {getByKeyPath, parsePath} from 'Quoting/utils/utilities';
import {createAction} from 'Shared/utilities/actionCreator';
import {filterWithIndex} from 'fp-ts/lib/Record';
import {pipe} from 'fp-ts/lib/pipeable';
import {Map as ImmutableMap, List as ImmutableList} from 'immutable';

export enum CreateQuoteActionType {
  UPDATE_SELECTED_PLAN = 'quoteForm/UPDATE_SELECTED_PLAN',
  SET_SELECTED_PRODUCT = 'quoteForm/SET_SELECTED_PRODUCT',
  SET_SELECTED_COVERAGE_INDEX = 'quoteForm/SET_SELECTED_COVERAGE_INDEX',
  RETRIEVE_PLANS_WITH_COVERAGES = 'quoteForm/RETRIEVE_PLANS_WITH_COVERAGES',
  CHANGE_PLANS_WITH_COVERAGES = 'quoteForm/CHANGE_PLANS_WITH_COVERAGES',
  LIFE_COVERAGE_SELECTED = 'quoteForm/LIFE_COVERAGE_SELECTED',
  INITIATE_CREATE_QUOTE = 'quoteForm/INITIATE_CREATE_QUOTE',
  SET_MASTER_POLICY_QUOTE = 'quoteForm/SET_MASTER_POLICY_QUOTE',
  UPDATE_MASTER_POLICY_QUOTE = 'quoteForm/UPDATE_MASTER_POLICY_QUOTE',
  LOAD_A360_LOOKUP = 'quoteForm/LOAD_A360_LOOKUP',
  UPDATE_A360_LOOKUPS = 'quoteForm/UPDATE_A360_LOOKUPS',
  GET_QUOTE_DETAILS = 'quoteForm/GET_QUOTE_DETAILS',
  CLEAR_SELECTED_PLANS = 'quoteForm/CLEAR_SELECTED_PLANS',
  UPDATE_QUOTE = 'quoteForm/UPDATE_QUOTE',
  CLEAR_SELECTED_PLAN = 'quoteForm/CLEAR_SELECTED_PLAN',
  RETRIEVE_ATTRIBUTE_VALUES = 'quoteForm/RETRIEVE_ATTRIBUTE_VALUES',
  CHANGE_ATTRIBUTE_VALUES = 'quoteForm/CHANGE_ATTRIBUTE_VALUES',
  CHANGE_ATTRIBUTE_VALUES_BY_COVERAGE = 'quoteForm/CHANGE_ATTRIBUTE_VALUES_NESTED',
  RETRIEVE_PLAN_CONFIGURATION = 'quoteForm/RETRIEVE_PLAN_CONFIGURATION',
  UPDATE_PLAN_FORM_LOADING_STATE = 'quoteForm/UPDATE_PLAN_FORM_LOADING_STATE',
  UPDATE_BACKDROP = 'quoteForm/UPDATE_BACKDROP',
  ADD_PLAN_TO_QUOTE = 'quoteForm/ADD_PLAN_TO_QUOTE',
  ADD_PLAN_TO_QUOTE_ACC = 'quoteForm/ADD_PLAN_TO_QUOTE_ACC',
  RETRIEVE_PLAN_INFORMATION_TO_ADD = 'quoteForm/RETRIEVE_PLAN_INFORMATION_TO_ADD',
  UPDATE_DEFAULT_VALUES_FOR_SELECTED_PLAN = 'quoteForm/UPDATE_DEFAULT_VALUES_FOR_SELECTED_PLAN',
  FETCH_CUSTOMER_CENSUS_CLASSES = 'quoteForm/FETCH_CUSTOMER_CENSUS_CLASSES',
  UPDATE_CUSTOMER_CENSUS_CLASSES = 'quoteForm/UPDATE_CUSTOMER_CENSUS_CLASSES',
  CALCULATE_RATES = 'quoteForm/CALCULATE_RATES',
  UPDATE_IS_LOADING_QUOTE_FORM = 'quoteForm/UPDATE_IS_LOADING_QUOTE_FORM',
  UPDATE_ENTRY_POINT_VARIATION = 'quoteForm/UPDATE_ENTRY_POINT_VARIATION',
  UPDATE_QUOTE_FORM_ERRORS = 'quoteForm/UPDATE_QUOTE_FORM_ERRORS',
  GET_QUOTE_RATES_AND_PREMIUM = 'quoteForm/GET_QUOTE_RATES_AND_PREMIUM',
  UPDATE_QUOTE_RATES_AND_PREMIUM = 'quoteForm/UPDATE_QUOTE_RATES_AND_PREMIUM',
  UPDATE_IS_SAVING_QUOTE = 'quoteForm/UPDATE_IS_SAVING_QUOTE',
  UPDATE_IS_CALCULATING_RATES = 'quoteForm/UPDATE_IS_CALCULATING_RATES',
  CLEAR_QUOTE_FORM_ERRORS = 'quoteForm/CLEAR_QUOTE_FORM_ERRORS',
  CLEAR_QUOTE_FORM_DATA = 'quoteForm/CLEAR_QUOTE_FORM_DATA',
  VALIDATE_QUOTE_WITH_RATES_API = 'quoteForm/VALIDATE_QUOTE_WITH_RATES_API',
  REMOVE_QUOTE_FORM_ERROR_BY_KEY = 'quoteForm/REMOVE_QUOTE_FORM_ERROR_BY_KEY',
  UPDATE_OFFER = 'quoteForm/UPDATE_OFFER',
  EDIT_PLAN_NAME = 'quoteForm/EDIT_PLAN_NAME',
  UPDATE_QUOTE_AFTER_COMPENSATION = 'quoteForm/UPDATE_QUOTE_AFTER_COMPENSATION',
  REMOVE_QUOTE_FORM_ERROR_ENTRY = 'quoteForm/REMOVE_QUOTE_FORM_ERROR_ENTRY',
  REMOVE_QUOTE_FORM_ERROR_ENTRIES = 'quoteForm/REMOVE_QUOTE_FORM_ERROR_ENTRIES',
  SET_MIN_MAX_CONFIG = 'quoteForm/SET_MIN_MAX_CONFIG',
  SET_QUOTED_COMMISSION_HEAPED_PERCENT_RATES = 'quoteForm/SET_QUOTED_COMMISSION_HEAPED_PERCENT_RATES',
  SET_QUOTED_COMMISSION_FLAT_PERCENT_RATE = 'quoteForm/SET_QUOTED_COMMISSION_FLAT_PERCENT_RATE',
  SET_GPLU_AGE_BANDS_AND_SMOKING_FLAGS = 'quoteForm/SET_GPLU_AGE_BANDS_AND_SMOKING_FLAGS',
}

type EntryPointPayload = {
  productCd: string;
  entryPoint: string;
  quote: any;
};

export const retrievePlansWithCoverage = (productCd: A360ProductCd, requestData: RetrievePlansRequestData) =>
  createAction(CreateQuoteActionType.RETRIEVE_PLANS_WITH_COVERAGES, {productCd, requestData});

export const calculateRates = (
  quote: ImmutableMap<string, any>,
  customerCensusClasses: CensusClassDTO[],
  useEISValidation?: boolean
) => createAction(CreateQuoteActionType.CALCULATE_RATES, {quote, customerCensusClasses, useEISValidation});

export const changeRetrievedPlansWithCoverage = (plansWithCoverages: Async<RetreivedPlan[]>) =>
  createAction(CreateQuoteActionType.CHANGE_PLANS_WITH_COVERAGES, {plansWithCoverages});

export const changeSelectedLifeCoverage = (selectedLifeCoverage: string) =>
  createAction(CreateQuoteActionType.LIFE_COVERAGE_SELECTED, {selectedLifeCoverage});

export const updateSelectedPlan = (plan: {planKey?: string; planIndex: number}) =>
  createAction(CreateQuoteActionType.UPDATE_SELECTED_PLAN, {plan});

export const clearSelectedPlans = () => createAction(CreateQuoteActionType.CLEAR_SELECTED_PLAN);

export const retrievePlanDataToAdd = (requestPayload: RetrievePlanConfigurationRequestPayload) =>
  createAction(CreateQuoteActionType.RETRIEVE_PLAN_INFORMATION_TO_ADD, {requestPayload});

export const updateIsLoadingQuoteForm = (loadingState: boolean) =>
  createAction(CreateQuoteActionType.UPDATE_IS_LOADING_QUOTE_FORM, {loadingState});

export const addPlanToQuoteHI = (planCd: string) => createAction(CreateQuoteActionType.ADD_PLAN_TO_QUOTE, {planCd});

export const addPlanToQuoteACC = (planCd: string) =>
  createAction(CreateQuoteActionType.ADD_PLAN_TO_QUOTE_ACC, {planCd});

export const getQuoteDetails = (
  productModel: A360ProductModel,
  rootId: string,
  revisionNo: number,
  opportunityId: string,
  shouldRedirect: boolean = true
) =>
  createAction(CreateQuoteActionType.GET_QUOTE_DETAILS, {
    productModel,
    rootId,
    revisionNo,
    opportunityId,
    shouldRedirect,
  });

export const initCreateQuote = (
  productModel: A360ProductModel,
  createQuotePayload: CreateQuoteRequestPayload,
  opportunityId: string
) => createAction(CreateQuoteActionType.INITIATE_CREATE_QUOTE, {productModel, createQuotePayload, opportunityId});

export const updateMasterPolicyQuote = (quoteData: Async<ImmutableMap<string, MasterPolicyQuote>>) =>
  createAction(CreateQuoteActionType.UPDATE_MASTER_POLICY_QUOTE, {quoteData});

export const loadA360Lookup = (lookupName: string) =>
  createAction(CreateQuoteActionType.LOAD_A360_LOOKUP, {lookupName});

export const updateA360Lookup = (lookupName: string, lookup: QuoteFormDropdownOption[]) =>
  createAction(CreateQuoteActionType.UPDATE_A360_LOOKUPS, {lookupName, lookup});

export const retrieveAttributeValues = (retrieveAttributeValuesPayload: RetrievePlanConfigurationRequestPayload) =>
  createAction(CreateQuoteActionType.RETRIEVE_PLAN_CONFIGURATION, {retrieveAttributeValuesPayload});

export const updateDefaultValuesForSelectedPlan = (planCd: string, defaultValues: ImmutableMap<string, any>) =>
  createAction(CreateQuoteActionType.UPDATE_DEFAULT_VALUES_FOR_SELECTED_PLAN, {planCd, defaultValues});

export const changeAttributeValues = (attributeValues: ImmutableMap<string, QuoteFormDropdownOption[]>) =>
  createAction(CreateQuoteActionType.CHANGE_ATTRIBUTE_VALUES, {attributeValues});

export const changeAttributeValuesByCoverage = (
  attributeValuesNested: ImmutableMap<string, ImmutableMap<string, QuoteFormDropdownOption[]>>
) => createAction(CreateQuoteActionType.CHANGE_ATTRIBUTE_VALUES_BY_COVERAGE, {attributeValuesNested});

export const updatePlanFormLoadingState = (isLoading: boolean) =>
  createAction(CreateQuoteActionType.UPDATE_PLAN_FORM_LOADING_STATE, {isLoading});

export const fetchCustomerCensusClasses = (isLoading: boolean) =>
  createAction(CreateQuoteActionType.FETCH_CUSTOMER_CENSUS_CLASSES, {isLoading});

export const updateBackdropState = (isLoading: boolean) =>
  createAction(CreateQuoteActionType.UPDATE_BACKDROP, {isLoading});

export const updateCustomerCensusClasses = (censusClasses: CensusClassDTO[]) =>
  createAction(CreateQuoteActionType.UPDATE_CUSTOMER_CENSUS_CLASSES, {censusClasses});

export const editPlanName = () => createAction(CreateQuoteActionType.EDIT_PLAN_NAME);

export const setSelectedProduct = (product: Product) =>
  createAction(CreateQuoteActionType.SET_SELECTED_PRODUCT, product);

export const updateQuote = (shouldExit?: boolean, opportunityId?: string) =>
  createAction(CreateQuoteActionType.UPDATE_QUOTE, {shouldExit, opportunityId});

export const updateQuoteAfterCompensation = (quote: Async<MasterPolicyQuote>) =>
  createAction(CreateQuoteActionType.UPDATE_QUOTE_AFTER_COMPENSATION, {quote});

export const updateIsSavingQuote = (isSavingQuote: boolean) =>
  createAction(CreateQuoteActionType.UPDATE_IS_SAVING_QUOTE, {isSavingQuote});

export const updateIsCalculatingRates = (isCalculatingRates: boolean) =>
  createAction(CreateQuoteActionType.UPDATE_IS_CALCULATING_RATES, {isCalculatingRates});

export const updateEntryPointVariation = (payload: EntryPointPayload) =>
  createAction(CreateQuoteActionType.UPDATE_ENTRY_POINT_VARIATION, {...payload});

export const updateOffer = (onComplete?: () => void) => createAction(CreateQuoteActionType.UPDATE_OFFER, {onComplete});

export const updateQuoteFormErrors = (errors: Record<string, any>) =>
  createAction(CreateQuoteActionType.UPDATE_QUOTE_FORM_ERRORS, {errors});

export const removeQuoteFormErrorEntry = (key: string) =>
  createAction(CreateQuoteActionType.REMOVE_QUOTE_FORM_ERROR_ENTRY, {key});

export const removeQuoteFormErrorEntries = (keys: string[]) =>
  createAction(CreateQuoteActionType.REMOVE_QUOTE_FORM_ERROR_ENTRIES, {keys});

export const clearQuoteFormErrors = () => createAction(CreateQuoteActionType.CLEAR_QUOTE_FORM_ERRORS);

export const getQuoteRatesAndPremium = (productCd: string, rootId: string, revisionNo: number) =>
  createAction(CreateQuoteActionType.GET_QUOTE_RATES_AND_PREMIUM, {productCd, rootId, revisionNo});

export const updateQuoteRatesAndPremiumInformation = (rateAndPremiumDetails: RatePremiumDetails | undefined) =>
  createAction(CreateQuoteActionType.UPDATE_QUOTE_RATES_AND_PREMIUM, {rateAndPremiumDetails});

export const clearQuoteFormData = () => createAction(CreateQuoteActionType.CLEAR_QUOTE_FORM_DATA);

export const setSelectedCoverageIndex = (coverageIndex: number) =>
  createAction(CreateQuoteActionType.SET_SELECTED_COVERAGE_INDEX, {coverageIndex});

export const validateQuoteWithRating = (
  quote: ImmutableMap<string, any>,
  customerCensusClasses: CensusClassDTO[],
  fieldKey?: string
) => createAction(CreateQuoteActionType.VALIDATE_QUOTE_WITH_RATES_API, {quote, customerCensusClasses, fieldKey});

export const setMinMaxConfig = (minMaxConfig: Record<string, EntityMinMaxConfig>) =>
  createAction(CreateQuoteActionType.SET_MIN_MAX_CONFIG, {minMaxConfig});

export const setQuotedCommissionHeapedPercentRate = (
  quotedCommissionFYRate: number,
  quotedCommissionRenewalRate: number
) =>
  createAction(CreateQuoteActionType.SET_QUOTED_COMMISSION_HEAPED_PERCENT_RATES, {
    quotedCommissionFYRate,
    quotedCommissionRenewalRate,
  });

export const setQuotedCommissionFlatPercentRate = (quotedCommissionRate: number) =>
  createAction(CreateQuoteActionType.SET_QUOTED_COMMISSION_FLAT_PERCENT_RATE, {
    quotedCommissionRate,
  });

export const setGpluAgeBandsAndSmokingFlags = (ageBandsAndSmokingFlags: GpluAgeBandsAndSmokingFlags) =>
  createAction(CreateQuoteActionType.SET_GPLU_AGE_BANDS_AND_SMOKING_FLAGS, {ageBandsAndSmokingFlags});

type CreateQuoteAction =
  | ReturnType<typeof updateSelectedPlan>
  | ReturnType<typeof setSelectedProduct>
  | ReturnType<typeof retrievePlansWithCoverage>
  | ReturnType<typeof changeRetrievedPlansWithCoverage>
  | ReturnType<typeof changeSelectedLifeCoverage>
  | ReturnType<typeof initCreateQuote>
  | ReturnType<typeof updateMasterPolicyQuote>
  | ReturnType<typeof updateA360Lookup>
  | ReturnType<typeof loadA360Lookup>
  | ReturnType<typeof retrieveAttributeValues>
  | ReturnType<typeof changeAttributeValues>
  | ReturnType<typeof changeAttributeValuesByCoverage>
  | ReturnType<typeof updateDefaultValuesForSelectedPlan>
  | ReturnType<typeof updatePlanFormLoadingState>
  | ReturnType<typeof updateBackdropState>
  | ReturnType<typeof fetchCustomerCensusClasses>
  | ReturnType<typeof updateCustomerCensusClasses>
  | ReturnType<typeof addPlanToQuoteHI>
  | ReturnType<typeof addPlanToQuoteACC>
  | ReturnType<typeof updateIsLoadingQuoteForm>
  | ReturnType<typeof clearSelectedPlans>
  | ReturnType<typeof updateEntryPointVariation>
  | ReturnType<typeof updateQuoteRatesAndPremiumInformation>
  | ReturnType<typeof updateIsSavingQuote>
  | ReturnType<typeof updateIsCalculatingRates>
  | ReturnType<typeof clearQuoteFormErrors>
  | ReturnType<typeof clearQuoteFormData>
  | ReturnType<typeof removeQuoteFormErrorEntry>
  | ReturnType<typeof removeQuoteFormErrorEntries>
  | ReturnType<typeof updateQuoteAfterCompensation>
  | ReturnType<typeof updateQuoteFormErrors>
  | ReturnType<typeof setSelectedCoverageIndex>
  | ReturnType<typeof setMinMaxConfig>
  | ReturnType<typeof setQuotedCommissionFlatPercentRate>
  | ReturnType<typeof setQuotedCommissionHeapedPercentRate>
  | ReturnType<typeof setGpluAgeBandsAndSmokingFlags>;

export type QuoteFormState = {
  attributeValues: ImmutableMap<string, QuoteFormDropdownOption[]>;
  attributeValuesByCoverage: ImmutableMap<string, ImmutableMap<string, QuoteFormDropdownOption[]>>;
  customerCensusClasses: CensusClassDTO[];
  defaultPlanValues: ImmutableMap<string, ImmutableMap<string, any>>;
  isBackDropOpen: boolean;
  isCalculatingRates: boolean;
  isLoadingCreateQuotePage: boolean;
  isLoadingPlanForm: boolean;
  isSavingQuote: boolean;
  lookups: ImmutableMap<string, QuoteFormDropdownOption[]>;
  opportunityId: string;
  plansWithCoverages: Async<RetreivedPlan[]>;
  selectedLifeCoverage: string;
  quote: Async<ImmutableMap<string, any>>;
  quoteFormErrors: Record<string, string | undefined>;
  quotePremiumDetails?: any;
  quoteRatesAndPremium?: RatePremiumDetails;
  selectedPlan?: {planKey?: string; planIndex: number};
  selectedCoverageIndex?: number;
  selectedProduct?: Product;
  minMaxConfig?: Record<string, EntityMinMaxConfig>;
  gpluAgeBandsAndSmokingFlags?: GpluAgeBandsAndSmokingFlags;
};

const INITIAL_QUOTING_FORM_STATE: QuoteFormState = {
  attributeValues: ImmutableMap(),
  attributeValuesByCoverage: ImmutableMap(),
  customerCensusClasses: [],
  defaultPlanValues: ImmutableMap(),
  isBackDropOpen: false,
  isCalculatingRates: false,
  isLoadingCreateQuotePage: false,
  isLoadingPlanForm: false,
  isSavingQuote: false,
  lookups: ImmutableMap(),
  opportunityId: undefined,
  plansWithCoverages: new Empty(),
  selectedLifeCoverage: undefined,
  quote: new Empty(),
  quoteFormErrors: {},
  quotePremiumDetails: undefined,
  quoteRatesAndPremium: undefined,
  selectedPlan: undefined,
  selectedCoverageIndex: undefined,
  selectedProduct: undefined,
  minMaxConfig: undefined,
  gpluAgeBandsAndSmokingFlags: undefined,
};

const quoteFormReducer = (
  state: QuoteFormState = INITIAL_QUOTING_FORM_STATE,
  action: CreateQuoteAction
): QuoteFormState => {
  switch (action.type) {
    case CreateQuoteActionType.UPDATE_MASTER_POLICY_QUOTE:
      if (
        isLoaded(action.payload.quoteData) &&
        (action.payload.quoteData.Data?.getIn(parsePath('benefitPackagingDetail.plans')) as ImmutableList<any>)?.size >
          0
      ) {
        return {
          ...state,
          quote: action.payload.quoteData,
          selectedPlan: {
            planIndex: state.selectedPlan?.planIndex ?? 0,
            planKey: action.payload.quoteData.Data?.getIn(
              parsePath(`benefitPackagingDetail.plans[${state.selectedPlan?.planIndex ?? 0}]._key.id`)
            ) as string,
          },
        };
      }
      return {
        ...state,
        quote: action.payload.quoteData,
      };

    case CreateQuoteActionType.CHANGE_PLANS_WITH_COVERAGES:
      return {
        ...state,
        plansWithCoverages: action.payload.plansWithCoverages,
      };

    case CreateQuoteActionType.LIFE_COVERAGE_SELECTED:
      return {
        ...state,
        selectedLifeCoverage: action.payload.selectedLifeCoverage,
      };

    case CreateQuoteActionType.UPDATE_A360_LOOKUPS:
      return {
        ...state,
        lookups: state.lookups.setIn([`${action.payload.lookupName}`], action.payload.lookup),
      };

    case CreateQuoteActionType.UPDATE_IS_LOADING_QUOTE_FORM:
      return {
        ...state,
        isLoadingCreateQuotePage: action.payload.loadingState,
      };

    case CreateQuoteActionType.CHANGE_ATTRIBUTE_VALUES:
      return {
        ...state,
        attributeValues: state.attributeValues.merge(action.payload.attributeValues),
      };

    case CreateQuoteActionType.CHANGE_ATTRIBUTE_VALUES_BY_COVERAGE:
      return {
        ...state,
        attributeValuesByCoverage: state.attributeValuesByCoverage.merge(action.payload.attributeValuesNested),
      };

    case CreateQuoteActionType.UPDATE_PLAN_FORM_LOADING_STATE:
      return {
        ...state,
        isLoadingPlanForm: action.payload.isLoading,
      };

    case CreateQuoteActionType.UPDATE_BACKDROP:
      return {
        ...state,
        isBackDropOpen: action.payload.isLoading,
      };

    case CreateQuoteActionType.UPDATE_DEFAULT_VALUES_FOR_SELECTED_PLAN:
      return {
        ...state,
        defaultPlanValues: state.defaultPlanValues.set(action.payload.planCd, action.payload.defaultValues),
      };

    case CreateQuoteActionType.ADD_PLAN_TO_QUOTE: {
      const quoteWithPlanInformation = addNewPlanToQuote(
        state.quote,
        state.defaultPlanValues.get(action.payload.planCd),
        state.plansWithCoverages,
        action.payload.planCd
      );

      if (isLoaded(quoteWithPlanInformation)) {
        return {
          ...state,
          quote: quoteWithPlanInformation,
          selectedPlan: {
            planKey: quoteWithPlanInformation.Data?.getIn(
              parsePath('benefitPackagingDetail.plans[0]._key.id')
            ) as string,
            planIndex: 0,
          },
          quoteFormErrors: {...state.quoteFormErrors, [MISSING_PLAN_ERROR_KEY]: undefined},
        };
      }

      return {
        ...state,
      };
    }

    case CreateQuoteActionType.ADD_PLAN_TO_QUOTE_ACC: {
      const accQuoteWithPlanInformation = addNewPlanToQuoteACC(
        state.quote,
        state.defaultPlanValues.get(action.payload.planCd),
        state.plansWithCoverages,
        action.payload.planCd
      );

      if (isLoaded(accQuoteWithPlanInformation)) {
        return {
          ...state,
          quote: accQuoteWithPlanInformation,
          selectedPlan: {
            planKey: accQuoteWithPlanInformation.Data?.getIn(
              parsePath('benefitPackagingDetail.plans[0]._key.id')
            ) as string,
            planIndex: 0,
          },
        };
      }

      return {
        ...state,
      };
    }
    case CreateQuoteActionType.UPDATE_CUSTOMER_CENSUS_CLASSES:
      return {
        ...state,
        customerCensusClasses: action.payload.censusClasses,
      };

    case CreateQuoteActionType.UPDATE_IS_SAVING_QUOTE:
      return {
        ...state,
        isSavingQuote: action.payload.isSavingQuote,
      };

    case CreateQuoteActionType.UPDATE_IS_CALCULATING_RATES:
      return {
        ...state,
        isCalculatingRates: action.payload.isCalculatingRates,
      };

    case CreateQuoteActionType.UPDATE_SELECTED_PLAN:
      return {
        ...state,
        selectedPlan: action.payload.plan,
      };

    case CreateQuoteActionType.CLEAR_SELECTED_PLAN:
      return {
        ...state,
        selectedPlan: undefined,
      };

    case CreateQuoteActionType.SET_SELECTED_PRODUCT:
      return {
        ...state,
        selectedProduct: action.payload,
      };

    case CreateQuoteActionType.SET_SELECTED_COVERAGE_INDEX:
      return {
        ...state,
        selectedCoverageIndex: action.payload.coverageIndex,
      };

    case CreateQuoteActionType.UPDATE_QUOTE_FORM_ERRORS: {
      const updatedQuoteFormErrors = {...state.quoteFormErrors, ...action.payload.errors};

      if (isLoaded(state.quote)) {
        const planKeys = (getByKeyPath(state.quote.Data, 'benefitPackagingDetail.plans') as ImmutableList<any>)
          ?.map(plan => getByKeyPath(plan, '_key.id'))
          ?.toArray();

        const cleanedQuoteFormErrors = pipe(
          updatedQuoteFormErrors,
          filterWithIndex(
            key =>
              (key.startsWith('plan') && planKeys?.some(planKey => key.includes(planKey as string))) ||
              !key.startsWith('plan')
          )
        );

        return {
          ...state,
          quoteFormErrors: cleanedQuoteFormErrors,
        };
      }

      return {
        ...state,
        quoteFormErrors: updatedQuoteFormErrors,
      };
    }

    case CreateQuoteActionType.REMOVE_QUOTE_FORM_ERROR_ENTRY:
      return {
        ...state,
        quoteFormErrors: {...state.quoteFormErrors, [action.payload.key]: undefined},
      };

    case CreateQuoteActionType.REMOVE_QUOTE_FORM_ERROR_ENTRIES:
      return {
        ...state,
        quoteFormErrors: Object.keys(state.quoteFormErrors).reduce((acc, key) => {
          if (!action.payload.keys.includes(key)) {
            acc[key] = state.quoteFormErrors[key];
          }
          return acc;
        }, {}),
      };

    case CreateQuoteActionType.CLEAR_QUOTE_FORM_DATA:
      return INITIAL_QUOTING_FORM_STATE;

    case CreateQuoteActionType.CLEAR_QUOTE_FORM_ERRORS:
      return {
        ...state,
        quoteFormErrors: {},
      };

    case CreateQuoteActionType.UPDATE_QUOTE_RATES_AND_PREMIUM:
      return {
        ...state,
        quoteRatesAndPremium: action.payload.rateAndPremiumDetails,
      };

    case CreateQuoteActionType.SET_MIN_MAX_CONFIG:
      return {
        ...state,
        minMaxConfig: action.payload.minMaxConfig,
      };

    case CreateQuoteActionType.SET_QUOTED_COMMISSION_HEAPED_PERCENT_RATES:
      return {
        ...state,
        quote: isLoaded(state.quote)
          ? new Loaded(
              state.quote.Data?.withMutations(quoteData => {
                quoteData?.set('quotedCommissionFYRate', action.payload.quotedCommissionFYRate);
                quoteData?.set('quotedCommissionRenewalRate', action.payload.quotedCommissionRenewalRate);
                quoteData?.delete('quotedCommissionRate');
              })
            )
          : state.quote,
      };

    case CreateQuoteActionType.SET_QUOTED_COMMISSION_FLAT_PERCENT_RATE:
      return {
        ...state,
        quote: isLoaded(state.quote)
          ? new Loaded(
              state.quote.Data?.withMutations(quoteData => {
                quoteData?.set('quotedCommissionRate', action.payload.quotedCommissionRate);
                quoteData?.delete('quotedCommissionFYRate');
                quoteData?.delete('quotedCommissionRenewalRate');
              })
            )
          : state.quote,
      };

    case CreateQuoteActionType.SET_GPLU_AGE_BANDS_AND_SMOKING_FLAGS:
      return {
        ...state,
        gpluAgeBandsAndSmokingFlags: action.payload.ageBandsAndSmokingFlags,
      };

    default:
      return state;
  }
};
export default quoteFormReducer;
